import {gpsControlAllows,gpsControlQueries,YOGU_PRO_READBACK_TRIAL,YOGU_PRO_36_PROFILE,yoguDriveControlModel} from './gps-control-profile.js';

function readableReply(reply) {
  return typeof reply==='string'&&reply.length<=4096&&!/CMD_FAILD|CMD_FAILED|CMD_END_FLAG_ERROR/i.test(reply)&&/^[\x20-\x7e\r\n\t]*$/.test(reply);
}
function proReadback(context) {
  return [YOGU_PRO_READBACK_TRIAL,YOGU_PRO_36_PROFILE].includes(context?.profile?.gpsControl?.id)&&gpsControlAllows(context.profile,'parameters');
}
function driveReadback(context){return Boolean(yoguDriveControlModel(context?.profile?.gpsControl?.id)&&gpsControlAllows(context.profile,'parameters'));}
// R26/R27 document the requests, but not their reply grammar. Keep the reply
// as text only until an independently inspected physical response establishes it.
export function reportedGpsRawAlarmReply(reply,context={}){
 return driveReadback(context)&&gpsControlQueries(context.profile).includes(context.query)
  &&['batteryAlarm','speedAlarm'].includes(context.query)&&readableReply(reply)&&reply.trim()
  ? reply.trim() : null;
}

function reportedSimFields(reply){
 const fields=reply.trim().split(',').map(field=>field.trim().match(/^(imei|imsi|iccid)=(.*)$/i)).filter(Boolean);
 if(!fields.length)return [];
 /** @type {Array<[string, RegExp]>} */
 const formats=[['imei',/^\d{15}$/],['imsi',/^\d{14,15}$/],['iccid',/^\d{18,22}$/]];
 return formats.map(([key,format])=>{
  const values=fields.filter(field=>field[1].toLowerCase()===key);
  // Each identity is independent. Missing, malformed or duplicate fields are
  // explicitly unknown; a non-decimal ICCID must not erase a usable IMEI/IMSI.
  return {key,value:values.length===1&&format.test(values[0][2])?values[0][2]:null,unit:null};
 });
}
// VER remains explicit. A bare VERSION reply is accepted only for the registered,
// model-bound Pro readback profile and the format observed on the physical unit.
// Display does not grant capabilities, update inventory or establish hardware.
export function reportedGpsFirmware(reply,context={}) {
  if(!readableReply(reply))return null;
  const fields=[...reply.matchAll(/(?:^|[,;\r\n])\s*VER:([^,;\r\n]{1,100})(?=[,;\r\n]|$)/gi)];
  if(fields.length===1){const value=fields[0][1].trim();return value&&/^[\x20-\x7e]+$/.test(value)?value:null;}
  if(fields.length||!(proReadback(context)||driveReadback(context))||context.query!=='version')return null;
  const value=reply.trim();
  if(driveReadback(context))return /^T89SA?_V\d{1,3}(?:\.\d{1,3}){1,3}$/.test(value)?value:null;
  return /^GT76E_V\d{1,3}(?:\.\d{1,3}){1,3}$/.test(value)?value:null;
}

// Preserve the returned names/values. Only the explicit 's' in TIMER establishes
// seconds. PARAM's HBT/TIMER numbers are not assigned units or operating semantics.
export function reportedGpsParameters(reply,context={}) {
  if(!readableReply(reply))return [];
  const drive=driveReadback(context);
  if(drive&&['vibrationAlarm','autoArming','batteryAlarm','speedAlarm'].includes(context.query)){
    if(!gpsControlQueries(context.profile).includes(context.query))return [];
    if(context.query==='vibrationAlarm'){
      const m=reply.trim().match(/^SENALM:(OFF|ON,([012]))$/i);
      return !m?[]:[{key:'vibrationAlarm',value:m[2]===undefined?'OFF':'ON',unit:null},
        ...(m[2]===undefined?[]:[{key:'alarmDelivery',value:m[2],unit:null}])];
    }
    if(context.query==='autoArming'){
      const m=reply.trim().match(/^DEFENSE:(\d{1,2})$/i);
      return m&&Number(m[1])<=60?[{key:'armingDelay',value:m[1],unit:'minutes'}]:[];
    }
    return [];
  }
  if(!proReadback(context)&&!drive){
    const profile=context.profile;
    if(context.query!=='parameters'||!['T11','T12'].includes(profile?.model)||!gpsControlAllows(profile,'parameters'))return [];
    /** @type {Array<[string, RegExp]>} */
    const patterns=[['tm',/(?:^|,)\s*TM:(\d{1,6},\d{1,6})(?=,(?:[A-Za-z][A-Za-z0-9]*:|CMD_SUCCESS)|$)/gi],['ht',/(?:^|,)\s*HT:(\d{1,6})(?=,(?:[A-Za-z][A-Za-z0-9]*:|CMD_SUCCESS)|$)/gi]];
    return patterns.flatMap(([key,pattern])=>{const values=[...reply.matchAll(pattern)];return values.length===1?[{key,value:values[0][1],unit:null}]:[];});
  }
  if(drive&&['sleep','acc','sim'].includes(context.query)){
    if(context.query==='sleep'){
      const m=reply.trim().match(/^SENDS:(\d{1,3})$/i);
      return m&&Number(m[1])<=300?[{key:'sleepMinutes',value:m[1],unit:'minutes'}]:[];
    }
    if(context.query==='acc'){
      const m=reply.trim().match(/^YG,ACCSET:([012])$/i);
      return m?[{key:'accMode',value:m[1],unit:null}]:[];
    }
    return reportedSimFields(reply);
  }
  if(context.query==='status'){
    /** @type {Array<[string, RegExp]>} */
    const patterns=[['batteryVoltage',/^Battery:(\d{1,2}\.\d{1,3})V$/i],['gprs',/^GPRS:(Link Up|Link Down)$/i],['gps',/^GPS:(open|close)$/i],['gsmRaw',/^GSM Signal Level:(\d{1,3})$/i]];
    if(drive)patterns.push(['externalVoltage',/^Power voltage:(\d{1,2}\.\d{1,3})V$/i],['login',/^Login:([01])$/i],['acc',/^ACC:(ON|OFF)$/i],['relay',/^RELAY:([01])$/i]);
    if(drive)patterns[2]=['gps',/^GPS:(close|Successful positioning|ufixed)$/i];
    const fields=(drive?reply.trim().replace(/^\[(Sleep|Positioned|Not positioned)\]\s*/i,''):reply.trim()).split(/[;\r\n]+/);
    return patterns.flatMap(([key,pattern])=>{const values=fields.map(field=>field.trim().match(pattern)).filter(Boolean);return values.length===1?[{key,value:values[0][1],unit:['batteryVoltage','externalVoltage'].includes(key)?'volts':null}]:[];});
  }
  if(context.query==='interval'){
    const match=reply.trim().match(/^TIMER ACC ON:(\d{1,6})s,\s*ACC OFF:(\d{1,6})s$/i);
    return match?[{key:'accOn',value:match[1],unit:'seconds'},{key:'accOff',value:match[2],unit:'seconds'}]:[];
  }
  if(context.query!=='parameters')return [];
  const fields=reply.trim().split(/[;\r\n]+/),rows=[];
  /** @type {Array<[string, RegExp]>} */
  const patterns=[
    ['timer',/^TIMER:(\d{1,6},\d{1,6})$/i],
    ['heartbeat',/^HBT:(\d{1,6}(?:,\d{1,6})?)$/i],
    ['sleepMinutes',/^SENDS:(\d{1,6})$/i],
    ['timezone',/^TimeZone:([EW],\d{1,2},\d{1,2})$/i],
  ];
  for(const [key,pattern] of patterns){
    const values=fields.map(field=>field.trim().match(pattern)).filter(Boolean);
    if(values.length===1)rows.push({key,value:values[0][1],unit:null});
  }
  return rows;
}

export function gpsReportingExplanation(profile,da) {
  if(profile?.protocol!=='gt06'||!['D3','T11','T12'].includes(profile?.model))return '';
  return da?"Enheden kan sende færre positioner, når den ligger stille. Det valgte interval giver derfor ikke altid en ny position hver gang.":"The device may send fewer positions while stationary. The selected interval does not always produce a new position each time.";
}

export function gpsCommandError(code,da,seconds=60) {
  const pair={
    rateLimited:[`Vent ${seconds} sekunder. Anmodningen blev ikke sendt.`,`Wait ${seconds} seconds. The request was not sent.`],
    gt06_offline:["Anmodningen er ikke sendt, fordi enheden er offline. Prøv igen, når den har forbindelse.","The request was not sent because the device is offline. Try again when it reconnects."],
    gt06_command_pending:["En anden ændring afventer svar. Vent, og prøv igen.","Another change is waiting for a reply. Wait and try again."],
    devicePermission:["Du har ikke adgang til at ændre denne enhed.","You do not have permission to change this device."],
    feature_unavailable:["Indstillingen kan ikke ændres på denne enhed.","This setting cannot be changed on this device."],
    gt06_reply_timeout:["Anmodningen er sendt, men enheden svarede ikke. Kontrollér indstillingen, før du prøver igen.","The request was sent, but the change is unconfirmed. Check the setting before trying again."],
    gt06_connection_closed:["Forbindelsen blev afbrudt. Kontrollér, om ændringen blev gemt.","The connection was interrupted. Check whether the change was saved."],
    sessionRenewedRetry:["Du er logget ind igen. Prøv at gemme ændringen igen.","You are signed in again. Try saving the change again."],
  }[code];
  return (pair??['Resultatet kunne ikke bekræftes. Kontrollér forbindelsen og enheden før et nyt forsøg.','The result could not be confirmed. Check the connection and device before trying again.'])[da?0:1];
}
